import clsx from 'clsx';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'pill';
  size?: 'sm' | 'md';
  dot?: boolean;
}

export function Badge({ children, variant = 'default', size = 'sm', dot = false }: BadgeProps) {
  const variants = {
    default: 'bg-surface-tertiary border border-transparent text-text-secondary',
    pill: 'bg-surface-primary border border-border-light text-text-secondary',
    success: 'bg-success-light text-success-dark border border-transparent',
    warning: 'bg-warning-light text-warning-dark border border-transparent',
    error: 'bg-error-light text-error-dark border border-transparent',
    info: 'bg-info-light text-info-dark border border-transparent',
  };

  const dotColors: Record<typeof variant, string> = {
    default: 'bg-text-muted',
    pill: 'bg-text-muted',
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
    info: 'bg-info',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-body-xs gap-1.5',
    md: 'px-2.5 py-1 text-body-sm gap-2',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-sm font-medium whitespace-nowrap',
        variants[variant],
        sizes[size]
      )}
    >
      {dot && <span className={clsx('h-1.5 w-1.5 rounded-full', dotColors[variant])} />}
      {children}
    </span>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const statusVariants: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
    active: 'success',
    connected: 'success',
    completed: 'success',
    running: 'info',
    syncing: 'info',
    pending: 'warning',
    idle: 'default',
    inactive: 'default',
    disconnected: 'default',
    error: 'error',
    failed: 'error',
  };

  return <Badge variant={statusVariants[status.toLowerCase()] ?? 'default'}>{status}</Badge>;
}

interface DotBadgeProps {
  children: React.ReactNode;
  status?: 'active' | 'inactive' | 'warning' | 'error';
}

export function DotBadge({ children, status = 'active' }: DotBadgeProps) {
  const dotColors = {
    active: 'bg-success',
    inactive: 'bg-text-muted',
    warning: 'bg-warning',
    error: 'bg-error',
  };

  return (
    <span className="inline-flex items-center gap-1.5 text-body-sm text-text-secondary">
      <span className={clsx('h-1.5 w-1.5 rounded-full', dotColors[status])} />
      {children}
    </span>
  );
}
